import { supabase } from "@/integrations/supabase/client";
import type { Ad } from "@/lib/data";
import { rowToAd, type ListingRow } from "@/lib/listings";
import { slugify } from "@/lib/seo";

export type PublicProfile = {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  banner_url: string | null;
  created_at: string;
  last_seen_at?: string | null;
  verified?: boolean | null;
};

/** Bu profili izləyənlərin sayı. */
export async function fetchFollowStats(
  profileId: string,
  viewerId?: string | null,
): Promise<{ count: number; following: boolean }> {
  const { count } = await supabase
    .from("user_follows")
    .select("id", { count: "exact", head: true })
    .eq("following_id", profileId);
  let following = false;
  if (viewerId) {
    const { data } = await supabase
      .from("user_follows")
      .select("id")
      .eq("following_id", profileId)
      .eq("follower_id", viewerId)
      .maybeSingle();
    following = Boolean(data);
  }
  return { count: count ?? 0, following };
}

export async function toggleFollowUser(profileId: string, viewerId: string, follow: boolean) {
  if (follow) {
    const { error } = await supabase
      .from("user_follows")
      .insert({ follower_id: viewerId, following_id: profileId });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("user_follows")
      .delete()
      .eq("follower_id", viewerId)
      .eq("following_id", profileId);
    if (error) throw error;
  }
}

export function profileName(p: Pick<PublicProfile, "full_name" | "username"> | null): string {
  return p?.full_name?.trim() || p?.username?.trim() || "Albazar istifadəçisi";
}

/** İstifadəçi profilinin ictimai linki (id + ad). */
export function profileSlug(p: { id: string; full_name?: string | null; username?: string | null }) {
  const base = slugify(p.full_name ?? p.username ?? "");
  return base ? `${base}-${p.id}` : p.id;
}

export async function fetchPublicProfile(id: string): Promise<PublicProfile | null> {
  const { data, error } = await supabase.rpc("public_profile", { _id: id });
  if (error) throw error;
  const row = (data as PublicProfile[] | null)?.[0];
  return row ?? null;
}

/** İstifadəçinin bütün aktiv elanları. */
export async function fetchUserListings(userId: string): Promise<Ad[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(
      "id, user_id, title, description, price, negotiable, category, city, images, badge, status, views, created_at, badge_expires_at, badge_price, specs",
    )
    .eq("user_id", userId)
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []).map((r) => rowToAd(r as unknown as ListingRow));
}

export type ProfileRating = { average: number; count: number };

export async function fetchUserRating(userId: string): Promise<ProfileRating> {
  const { data, error } = await supabase
    .from("reviews")
    .select("rating")
    .eq("seller_id", userId)
    .eq("status", "approved");
  if (error) throw error;
  const rows = data ?? [];
  if (!rows.length) return { average: 0, count: 0 };
  const sum = rows.reduce((s, r) => s + Number(r.rating ?? 0), 0);
  return { average: sum / rows.length, count: rows.length };
}

export async function fetchUserStore(userId: string) {
  const { data, error } = await supabase
    .from("stores")
    .select("id, name, slug, verified, status")
    .eq("owner_id", userId)
    .eq("status", "active")
    .maybeSingle();
  if (error) return null;
  return data;
}
